// src/components/CookieConsent.jsx
/* global dataLayer */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { AUTH_API } from "@/config/api";

const LS_KEY = "byonco_cookie_consent_v1";
const DEFAULTS = { essential: true, analytics: false, marketing: false, time: 0 };

export default function CookieConsent({ brandClass = "bg-white/95 backdrop-blur-md text-charcoal border border-border-mist shadow-2xl" }) {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULTS);
  const [settings, setSettings] = useState(false);

  // Init + migrate old single-flag key
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY));
      if (saved) {
        setPrefs(saved);
        applyConsent(saved);
        return; // already decided; no banner
      }
      const legacy = localStorage.getItem("cookieConsent"); // 'accepted' | 'declined'
      if (legacy === "accepted" || legacy === "declined") {
        const migrated = {
          essential: true,
          analytics: legacy === "accepted",
          marketing: legacy === "accepted",
          time: Date.now(),
        };
        localStorage.setItem(LS_KEY, JSON.stringify(migrated));
        setPrefs(migrated);
        applyConsent(migrated);
        setOpen(false);
      } else {
        setOpen(true); // first visit
      }
    } catch {
      setOpen(true);
    }
  }, []);

  // ---- helper: SAFE GA env read (CRA/Jest-friendly, no import.meta) ----
  const getGAId = () => {
    if (typeof process !== "undefined" && typeof process.env !== "undefined") {
      // Prefer CRA-style env var, but also support Vite-style naming if present at build time
      return (
        process.env.REACT_APP_GA_ID_BYONCO ||
        process.env.VITE_GA_ID_BYONCO ||
        process.env.GA_ID_BYONCO ||
        undefined
      );
    }

    // Fallback: allow injection via a global if needed in non-Node environments
    if (typeof window !== "undefined" && window.__GA_ID_BYONCO__) {
      return window.__GA_ID_BYONCO__;
    }

    return undefined;
  };

  const save = (next) => {
    const payload = { ...next, time: Date.now() };
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(payload));
    } catch {}
    try {
      fetch(`${AUTH_API}/cookie-consent`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analytics: Boolean(payload.analytics),
          marketing: Boolean(payload.marketing),
          health_analytics: false,
        }),
      }).catch(() => {});
    } catch {}
    try {
      applyConsent(payload);
    } catch {
      // Never let GA/script errors block closing the banner
    }
  };

  // Enable/disable vendors based on consent (Google Analytics optional)
  const applyConsent = (c) => {
    const GA = getGAId(); // safe
    const existing = document.getElementById("ga-script");
    if (existing) existing.remove();

    const path = window.location.pathname || '/';
    const sensitive = ['/app', '/cancer', '/rare', '/second-opinion', '/teleconsultation', '/journey', '/cost-calculator', '/find-', '/genomics', '/records']
      .some((prefix) => path === prefix || path.startsWith(prefix));
    // Health-related paths are not sent to Analytics.
    if (c.analytics && GA && !sensitive) {
      const s = document.createElement("script");
      s.id = "ga-script";
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA}`;
      document.head.appendChild(s);

      const init = document.createElement("script");
      init.appendChild(
        document.createTextNode(`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA}', { anonymize_ip: true });
        `)
      );
      document.head.appendChild(init);
    }
  };

  const acceptAll = () => {
    const n = { essential: true, analytics: true, marketing: true };
    setPrefs(n);
    save(n);          // persist + (maybe) init GA
    setOpen(false);   // close banner no matter what
  };

  const rejectAll = () => {
    const n = { essential: true, analytics: false, marketing: false };
    setPrefs(n);
    save(n);
    setOpen(false);
  };

  const saveChoices = () => {
    save(prefs);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed inset-x-0 bottom-0 z-[9999] p-4 sm:p-6 pointer-events-none"
          aria-live="polite"
        >
          {/* Card */}
          <div
            className={`mx-auto max-w-4xl rounded-2xl ${brandClass} pointer-events-auto`}
            role="dialog"
            aria-labelledby="cookie-title"
          >
            {!settings ? (
              <div className="p-4 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center">
                <div className="flex-1">
                  <h3 id="cookie-title" className="text-base sm:text-lg font-bold text-forest-ink mb-1">
                    We value your privacy
                  </h3>
                  <p className="text-xs sm:text-sm text-charcoal/85 leading-relaxed">
                    Essential cookies run the site securely. Optional analytics help us improve patient care experiences. Manage choices anytime in Cookie
                    Settings. See our{" "}
                    <a href="/privacy" className="text-emerald-700 font-semibold underline hover:text-emerald-800">
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a href="/cookies" className="text-emerald-700 font-semibold underline hover:text-emerald-800">
                      Cookie Policy
                    </a>
                    .
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 sm:justify-end items-center">
                  <button
                    type="button"
                    onClick={() => setSettings(true)}
                    className="px-4 py-2 rounded-xl border border-border-mist bg-white hover:bg-mist text-forest-ink text-xs sm:text-sm font-semibold transition shadow-2xs"
                  >
                    Settings
                  </button>
                  <button
                    type="button"
                    onClick={rejectAll}
                    className="px-4 py-2 rounded-xl bg-mist hover:bg-emerald-50 text-charcoal text-xs sm:text-sm font-semibold transition"
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="px-5 py-2 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white text-xs sm:text-sm font-bold transition shadow-sm"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6">
                <h3 className="text-lg font-bold text-forest-ink mb-2">Cookie Settings</h3>
                <div className="space-y-4">
                  <Toggle label="Essential" desc="Required for core platform features & security." checked disabled />
                  <Toggle
                    label="Analytics"
                    desc="Anonymous usage analytics to help us optimize care navigation."
                    checked={prefs.analytics}
                    onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                  />
                  <Toggle
                    label="Marketing"
                    desc="Anonymous measurement and clinical search optimization."
                    checked={prefs.marketing}
                    onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                  />
                </div>
                <div className="mt-6 flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setSettings(false)}
                    className="px-4 py-2 rounded-xl border border-border-mist bg-white hover:bg-mist text-forest-ink text-xs sm:text-sm font-semibold transition"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={saveChoices}
                    className="px-5 py-2 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white text-xs sm:text-sm font-bold transition shadow-sm"
                  >
                    Save Preferences
                  </button>
                </div>
                <p className="mt-3 text-xs text-charcoal/60">
                  Questions? Contact us at{" "}
                  <a className="text-emerald-700 underline font-medium" href="mailto:care@getbyonco.com">
                    care@getbyonco.com
                  </a>
                  .
                </p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Toggle({ label, desc, checked = false, onChange = () => {}, disabled = false }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-70" : ""}`}>
      <input
        type="checkbox"
        className="mt-1 h-4 w-4"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-sm opacity-80">{desc}</div>
      </div>
    </label>
  );
}
